import { getIntl } from 'umi';

import { CoreEnums } from '@/utils/domain/constants';
import { Regexp } from '@/utils/infrastructure/utilities';
import BaseFieldHandler from '../BaseFieldHandler';

const { FormFieldType, FormFieldStringType, TextFormat } = CoreEnums;

export default class ValidationFieldHandler extends BaseFieldHandler {
  static supportedTypes = [
    FormFieldStringType.Url,
    FormFieldType.Url,
    FormFieldStringType.Email,
    FormFieldType.Email,
    FormFieldStringType.Password,
    FormFieldType.Password,
    FormFieldStringType.Text,
    FormFieldStringType.TextMultiple,
    FormFieldType.Text,
    FormFieldType.TextMultiple,
  ];

  handle() {
    const { type } = this.baseConfig;

    const handlers = {
      [FormFieldStringType.Url]: () => this.handleUrl(),
      [FormFieldType.Url]: () => this.handleUrl(),
      [FormFieldStringType.Email]: () => this.handleEmail(),
      [FormFieldType.Email]: () => this.handleEmail(),
      [FormFieldStringType.Password]: () => this.handlePassword(),
      [FormFieldType.Password]: () => this.handlePassword(),
      [FormFieldStringType.Text]: () => this.handleText(),
      [FormFieldStringType.TextMultiple]: () => this.handleText(),
      [FormFieldType.Text]: () => this.handleText(),
      [FormFieldType.TextMultiple]: () => this.handleText(),
    };

    const handler = handlers[type];
    if (!handler) {
      throw new Error(`Unsupported validation field type: ${type}`);
    }

    return handler();
  }

  handleUrl() {
    const config = this.getBaseConfig();
    const { isDetails } = this.fieldState;
    const { readOnly } = this.baseConfig;

    return {
      ...config,
      ...((isDetails || readOnly) && { type: 'url' }),
      ...(!isDetails &&
        !readOnly && {
          rules: [
            ...config.rules,
            () => ({
              validator(_, value) {
                return !value || Regexp.isValidUrl(value)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(getIntl().formatMessage({ id: 'input.validation.url' })),
                    );
              },
            }),
          ],
        }),
    };
  }

  handleEmail() {
    const config = this.getBaseConfig();

    return {
      ...config,
      type: 'email',
      rules: [
        {
          type: 'email',
          ...config.rules[0],
        },
      ],
    };
  }

  handlePassword() {
    const config = this.getBaseConfig();
    const { isDetails } = this.fieldState;

    return {
      ...config,
      type: 'password',
      ...(isDetails && {
        disabled: true,
        iconRender: () => null,
        style: { pointerEvents: 'none' },
        visibilityToggle: false,
      }),
    };
  }

  handleText() {
    const config = this.getBaseConfig();
    const { isDetails } = this.fieldState;
    const deeps = this.getDependencies();

    const { type, format, minLength, maxLength, textFormat, regularExpression } = this.baseConfig;

    const isTextMultiple =
      type === FormFieldType.TextMultiple || type === FormFieldStringType.TextMultiple;
    const isTextWithRegExp =
      regularExpression && (type === FormFieldType.Text || type === FormFieldStringType.Text);

    return {
      ...config,
      format,
      deeps,
      ...(minLength > 1 && { minLength }),
      ...(maxLength > 1 && { maxLength }),
      ...(isTextMultiple && { type: 'textarea' }),
      ...(textFormat === TextFormat.Html && {
        type: 'html-editor',
        isDetails,
        wrapperCol: { span: 24 },
        labelCol: { span: 24 },
      }),
      ...(textFormat === TextFormat.Markdown && {
        type: 'md-editor',
        isDetails,
        wrapperCol: { span: 24 },
        labelCol: { span: 24 },
      }),
      ...(isTextWithRegExp && {
        rules: [
          ...config.rules,
          () => ({
            validator(_, value) {
              return !value || new RegExp(regularExpression).test(value)
                ? Promise.resolve()
                : Promise.reject(
                    new Error(
                      getIntl().formatMessage(
                        { id: 'input.validation.regexp' },
                        { regexp: regularExpression },
                      ),
                    ),
                  );
            },
          }),
        ],
      }),
    };
  }
}
